const cloneDeep = require('lodash/cloneDeep');

const indexFields = [
  'calculatedIndex',
  'disabledIndex',
  'errorIndex',
  'hiddenIndex',
  'infoIndex',
  'invalidValue',
  'optionsIndex',
  'touchedIndex',
  'validIndex',
  'warningIndex',
];

class TableManager {
  constructor(size = 0, tableName = '') {
    // Name to id the table
    this.name = tableName;
    // Field Name List
    this.fieldNameList = [];
    // Unsorted list (rowIds)
    this.unsorted = [];
    // Sorted list (rowIds)
    this.sorted = this.unsorted;
    // rowId to dataIndex lookup
    this.rowIds = {};
    // Starting rowId
    this.startId = 1;
    // Form object from FormManager
    this.masterForm = {};
    // Sort direction ASC | DESC | null
    this.sortDirection = null;

    // Holds cached row data
    this.cachedRowData = {};
    // Indicates whether cacheRowData() has been completed
    this.cached = false;
    // List of rows that need to be re-cached
    this.killList = {};

    // List of field names that have dependencies
    this.depList = [];
    // Original Size of table
    this.originalSize = size;
    // rowIds that have been marked for deletion
    this.deleted = {};
    // Filter fieldName => value
    this.filter = {};
    // rowIds to be filtered
    this.filtered = {};

    // Pagination settings
    this.threshold = null;
    this.limit = null;

    this.rows = [];

    this.initSize(size);
  }

  initSize(size) {
    // Build initial rowId to dataIndex lookup lastRowId is stored so that
    // next rowId can easily be determined
    let dataIndex = 0;
    for (let id = this.startId; id < size + this.startId; id++) {
      this.rowIds[id] = dataIndex;
      this.lastRowId = id;
      dataIndex++;
      this.unsorted.push(id);
      this.sorted = this.unsorted;
    }

    if (this.lastRowId === undefined) {
      this.lastRowId = 0;
    }
  }

  /**
   * Get rowId with only the dataIndex
   *
   * @param {Number} dataIndex
   * @return {Number}
   */
  getRowIdByDataIndex(dataIndex) {
    const keys = Object.keys(this.rowIds).map(r => r / 1);

    for (let index = 0; index < keys.length; index++) {
      const rowId = keys[index];
      if (this.rowIds[rowId] === parseInt(dataIndex, 10)) {
        return rowId;
      }
    }

    return null;
  }

  /**
   * Get dataIndex with only the rowId
   *
   * @param {Number} rowId
   * @return {Number}
   */
  getDataIndexByRowId(rowId) {
    return this.rowIds[rowId];
  }

  hasBoundaries() {
    return this.start !== undefined && this.end !== undefined;
  }

  /**
   * For pagination, figure out start and ending indexes
   *
   * @param {Number} currentIndex - The known starting index.
   * @return {Array} - [start index, end index]
   */
  setBoundaries(currentIndex) {
    const rowIds = this.getRows();
    const { limit, threshold } = this;

    const overThreshold = rowIds.length >= threshold;
    const numOfRows = rowIds.length || 1 - 1;

    let start;
    let end;

    if (overThreshold) {
      start = currentIndex;
      end = start + limit;

      if (end > numOfRows) {
        end = numOfRows;
      }
    } else {
      start = 0;
      end = numOfRows;
    }

    this.start = start;
    this.end = end;

    return [this.start, this.end];
  }

  /**
   * Remove a row by dataIndex
   *
   * @param {Number} dataIndex
   * @return {TableManager}
   */
  removeByDataIndex(dataIndex) {
    const rowId = this.getRowIdByDataIndex(dataIndex);
    return this.removeRow(rowId);
  }

  /**
   * Remove a row by rowId
   *
   * @param {Number} rowId
   * @return {TableManager}
   */
  removeRow(rowId) {
    if (rowId < this.originalSize) {
      this.deleted[rowId] = true;
    } else {
      delete this.rowIds[rowId];
      delete this.cachedRowData[rowId];
    }

    let go = this.unsorted.length;
    while (go--) {
      if (this.unsorted[go] === rowId) {
        this.unsorted.splice(go, 1);
      }
    }

    this.reIndex();
    return this;
  }

  /**
   * Add a new row
   *
   * @param {Number} index
   * @return {TableManager}
   */
  addNewId(index = this.unsorted.length) {
    const newId = this.lastRowId + 1;
    this.lastRowId = newId;
    this.rowIds[newId] = index;
    this.unsorted.splice(index, 0, newId);

    // rowIds are out of date, increment any over the newly created index
    Object.keys(this.rowIds).forEach(rowId => {
      if (this.rowIds[rowId] >= index) {
        this.rowIds[rowId] += 1;
      }
    });

    this.reIndex();
    return newId;
  }

  /**
   * Get current, deleted, and total row counts
   *
   * @return {Object}
   */
  getCount() {
    const rowIds = Object.keys(this.rowIds);
    let count = 0;
    const deleted = Object.keys(this.deleted).length;
    let filtered = 0;

    rowIds.forEach(rowId => {
      if (this.filtered[rowId]) {
        filtered++;
        return;
      }

      count++;
    });
    return { count, deleted, total: count + filtered, filtered };
  }

  /**
   * Recreate rowIds lookup (not sure if this is necessary anymore)
   *
   * @return {Object}
   */
  reIndex() {
    this.rowIds = {};
    let dataIndex = 0;

    this.unsorted.forEach(rowId => {
      if (!this.deleted[rowId]) {
        this.rowIds[rowId] = dataIndex;
        dataIndex++;
      } else {
        this.rowIds[rowId] = null;
      }
    });

    if (this.filter) {
      this.applyFilter(this.filter);
    }

    if (this.sortedField) {
      this.sortBy(this.sortedField, this.sortDirection);
    }

    return this;
  }

  /**
   * For every field that has dependencies defined, determine if hidden has changed and kill
   * cache for that row
   * TODO This sucks
   *
   * @return {TableManager}
   */
  checkDeps() {
    const { rowIds } = this;
    const form = this.masterForm;
    const rowData = this.cachedRowData;

    if (!Object.keys(rowData).length) {
      return this;
    }

    this.depList.forEach(fieldName => {
      Object.keys(rowIds).forEach(rowId => {
        const dataIndex = this.getDataIndexByRowId(rowId);
        const currentRow = rowData[rowId];

        if (!currentRow) {
          return;
        }

        if (currentRow[fieldName].hidden !== form[fieldName].hiddenIndex[dataIndex]) {
          this.killCacheByRowId(rowId);
        }

        if (currentRow[fieldName].disabled !== form[fieldName].disabledIndex[dataIndex]) {
          this.killCacheByRowId(rowId);
        }

        if (currentRow[fieldName].allHidden !== form[fieldName].allHidden) {
          this.killCacheByRowId(rowId);
        }
      });
    });

    return this;
  }

  /**
   * Cache row data because cloning each time is too expensive
   * TODO This sucks
   *
   * @param {Number} limit - Cache row up to this number.
   * @return {TableManager}
   */
  cacheRowData() {
    const { start, end } = this;
    const form = this.masterForm;
    const rowData = this.cachedRowData || {};
    const rowIds = this.getRows();

    rowIds.forEach((rowId, index) => {
      // Only store what is to be viewable
      if (this.hasBoundaries() && (index < start || index > end)) {
        return;
      }

      // Check cache kill list and assign it an empty object (? Perhaps delete is better)
      const dataIndex = this.getDataIndexByRowId(rowId);
      if (this.killList[rowId] || !rowData[rowId]) {
        rowData[rowId] = {};
        this.killList[rowId] = false;
      } else {
        // rowData[rowId] = rowData[rowId];
      }

      // Reusing rowData objects, it'll get updated below from masterForm object
      const currentRow = rowData[rowId];

      Object.keys(form).forEach(fieldName => {
        if (form[fieldName].aggregated) {
          return;
        }

        // TODO Not optimal at all
        if (!currentRow[fieldName]) {
          currentRow[fieldName] = cloneDeep(form[fieldName]);

          // Delete Index fields as a patch for the above memory hog
          indexFields.forEach(idx => {
            if (currentRow[fieldName][idx]) {
              delete currentRow[fieldName][idx];
            }
          });
        }

        currentRow[fieldName].data = form[fieldName].data[dataIndex];
        currentRow[fieldName].displayValue = form[fieldName].displayValue[dataIndex];
        currentRow[fieldName].originalValue = form[fieldName].originalValue[dataIndex];
        currentRow[fieldName].originalDisplayValue = form[fieldName].originalDisplayValue[dataIndex];
        currentRow[fieldName].valid = form[fieldName].validIndex[dataIndex];
        currentRow[fieldName].errorMessage = form[fieldName].errorIndex[dataIndex];
        // TODO Shouldn't need this logic
        currentRow[fieldName].hidden =
          form[fieldName].hiddenIndex[dataIndex] !== undefined
            ? form[fieldName].hiddenIndex[dataIndex]
            : form[fieldName].hidden;
        currentRow[fieldName].disabled = form[fieldName].disabledIndex[dataIndex];
        currentRow[fieldName].touched = form[fieldName].touchedIndex[dataIndex];
        currentRow[fieldName].warning = form[fieldName].warningIndex[dataIndex];
        currentRow[fieldName].calculated = form[fieldName].calculatedIndex[dataIndex];
        currentRow[fieldName].info = form[fieldName].infoIndex[dataIndex];
        currentRow[fieldName].allHidden = form[fieldName].hiddenIndex.every(d => d === true);
        currentRow[fieldName].style = form[fieldName].style;

        if (form[fieldName].optionsIndex) {
          currentRow[fieldName].options = form[fieldName].optionsIndex[dataIndex];
        }
      });
    });

    this.cachedRowData = rowData;
    this.cached = true;
    this.setRows();

    return this;
  }

  /**
   * Dig through form object and find fields with dependencies
   * TODO This sucks
   *
   * @return {TableManager}
   */
  cacheDepList() {
    const form = this.masterForm;
    this.depList = [];

    Object.keys(form).forEach(fieldName => {
      if (form[fieldName].dependencies) {
        if (!this.depList) {
          this.depList = [];
        }
        this.depList.push(fieldName);
      }
    });
    return this;
  }

  /**
   * Mark row to be re-cached by rowId
   *
   * @return {TableManager}
   */
  killCacheByRowId(rowId) {
    this.killList[rowId] = true;
    return this;
  }

  /**
   * Mark row to be re-cached by dataIndex
   *
   * @return {TableManager}
   */
  killCacheByDataIndex(dataIndex) {
    return this.killCacheByRowId(this.getRowIdByDataIndex(dataIndex));
  }

  // TODO Probably not optimal
  applyFilter(filter) {
    this.filter = filter;
    this.filtered = {};

    const form = this.masterForm;
    const filterRegex = {};
    Object.keys(filter).forEach(fieldName => {
      filterRegex[fieldName] = new RegExp(filter[fieldName], 'gi');
    });

    Object.keys(this.rowIds).forEach(rowId => {
      if (this.deleted[rowId]) {
        return;
      }

      const dataIndex = this.rowIds[rowId];

      Object.keys(filter).forEach(fieldName => {
        // NOTE Added rows since last filter won't be filtered
        // Old way, didn't filter paginated tables
        // if (this.cachedRowData[rowId]) {
        //   const string = this.cachedRowData[rowId][fieldName].data;
        //   if (!string.match(filterRegex[fieldName])) {
        //     this.filtered[rowId] = true;
        //   }
        // }
        if (form[fieldName] && form[fieldName].data[dataIndex]) {
          const string = form[fieldName].data[dataIndex];
          if (!string.match(filterRegex[fieldName])) {
            this.filtered[rowId] = true;
          }
        }
      });
    });

    this.setRows();
    return this;
  }

  /**
   * Clear any filters
   *
   * @return {TableManager}
   */
  clearFilter() {
    this.filter = {};
    this.filtered = {};
    this.setRows();
    return this;
  }

  clearCache() {
    this.cachedRowData = {};
    this.cached = false;
    return this;
  }

  /**
   * Determine whether any rows are filtered
   *
   * @return {Boolean}
   */
  hasFilter() {
    return !!Object.keys(this.filtered).length;
  }

  /**
   * Get the last rowId for collection
   *
   * @return {Number}
   */
  getLastRowId() {
    return this.lastRowId;
  }

  /**
   * Returns next sort direction or off
   *
   * @return {String}
   */
  getNextSortDirection() {
    if (this.sortDirection === 'ASC') {
      return 'DESC';
    }

    if (this.sortDirection === 'DESC') {
      return null;
    }

    return 'ASC';
  }

  /**
   * Sort by a given field
   *
   * @param {String} fieldName
   * @return {TableManager}
   */
  sortBy(fieldName, direction) {
    const { data, type } = this.masterForm[fieldName];

    if (this.sortedField !== fieldName && !direction) {
      this.sortDirection = null;
    }

    this.sortDirection = direction || this.getNextSortDirection();
    this.sortedField = this.sortDirection ? fieldName : null;

    const isNumerical = ['currency', 'number', 'integer'].some(t => t === type);
    const sortType = isNumerical ? 'numerical' : 'lexical';

    const methods = {
      ASC: {
        lexical: (a, b) => (data[a] < data[b] ? -1 : (data[b] < data[a]) | 0), // eslint-disable-line
        numerical: (a, b) => data[a] - data[b],
      },
      DESC: {
        lexical: (a, b) => (data[a] > data[b] ? -1 : (data[b] > data[a]) | 0), // eslint-disable-line
        numerical: (a, b) => data[b] - data[a],
      },
    };

    let sorted;

    if (this.sortDirection) {
      sorted = Array.from(Array(data.length).keys()).sort(methods[this.sortDirection][sortType]);
    } else {
      this.sorted = this.unsorted;
      this.setRows();
      return this;
    }

    const rowIds = [];
    sorted.forEach(dataIndex => {
      rowIds.push(this.getRowIdByDataIndex(dataIndex));
    });

    this.sorted = rowIds;
    this.setRows();
    return this;
  }

  getRows() {
    return this.rows || [];
  }

  setRows() {
    const rows = [];

    this.sorted.forEach(rowId => {
      if (this.filtered[rowId] || this.deleted[rowId]) {
        return;
      }

      rows.push(rowId);
    });

    this.rows = rows;
    return rows;
  }

  forEachRendered(cb) {
    const rowIds = this.getRows();
    for (let index = this.start; index < this.end; index++) {
      cb(rowIds[index]);
    }
  }
}

export default TableManager;
